import { chronicleBridge } from '@/tauri/chronicle';
import type {
  BluetoothPermissionStatus,
  BluetoothStatus,
  ChronicleError,
} from '@/tauri/chronicle/types';

export type ChronicleBlePrereqResult =
  | { ok: true; status: BluetoothStatus }
  | {
      ok: false;
      error: ChronicleError;
      /** Present when checkBluetooth succeeded but a hard-stop bar failed. */
      permissionStatus?: BluetoothPermissionStatus;
      available?: boolean;
    };

/**
 * BLE preflight for reconnect / startup / DeviceSearch (skips network).
 * Permission hard-stop is denied-only — prompt_required and unknown fall through
 * so native scan / ensure_adapter can still raise the macOS Bluetooth prompt.
 * Radio hard-stop remains: adapter must be powered on (available).
 * Uses the shared Chronicle IPC reply budget (actor `IPC_REPLY_TIMEOUT`).
 */
export async function assertChronicleBlePrereqs(): Promise<ChronicleBlePrereqResult> {
  try {
    const status = await chronicleBridge.checkBluetooth();

    // Denied-only permission hard-stop. prompt_required / unknown fall through
    // so the native path can still surface the system Bluetooth prompt.
    if (status.permissionStatus === 'denied') {
      return {
        ok: false,
        permissionStatus: status.permissionStatus,
        available: status.available,
        error: {
          code: 'BLUETOOTH_PERMISSION_DENIED',
          message: 'settings.chronicle.error.bluetoothPermissionTitle',
          recoverable: true,
          guidance: 'settings.chronicle.error.bluetoothPermissionDescription',
        },
      };
    }

    if (!status.available) {
      return {
        ok: false,
        permissionStatus: status.permissionStatus,
        available: status.available,
        error: {
          code: 'BLUETOOTH_DISABLED',
          message: 'settings.chronicle.error.bluetoothOffTitle',
          recoverable: true,
          guidance: 'settings.chronicle.error.bluetoothOffDescription',
        },
      };
    }

    return { ok: true, status };
  } catch {
    return {
      ok: false,
      error: {
        code: 'UNKNOWN',
        message: 'settings.chronicle.error.unknownTitle',
        recoverable: true,
        guidance: 'settings.chronicle.error.unknownDescription',
      },
    };
  }
}
